/**
 * Messstellen einer Wohneinheit zuordnen **und** das Messkonzept speichern —
 * als ein Vorgang, so weit sich das von hier aus sagen lässt (HM-190).
 *
 * **Zwei Endpunkte, keine Transaktion.** Der Server verlangt, dass die Messstelle
 * im Steckplatz einer Wohnung dieser Wohnung zugeordnet ist (HM-082); deshalb
 * geht erst `PATCH /metering-points/{id}` hinaus, dann die Fassung. Scheitert
 * die Fassung, stünde die Messstelle sonst umgezogen da, ohne dass jemand es
 * wollte — der Nutzer hat „Umziehen **und** übernehmen" bestätigt, nicht das
 * eine ohne das andere. Darum wird zurückgestellt, in umgekehrter Reihenfolge.
 *
 * **Und was sich nicht zurückstellen lässt, wird benannt.** Ein zweiter Fehler
 * beim Zurückstellen ist selten und dann genau der Fall, in dem jemand wissen
 * muss, welcher Zähler jetzt wo hängt — still wäre das eine Zuordnung, die
 * später niemand erklären kann.
 */

export interface UnitMove {
  readonly meteringPointId: string;
  /** Der Name des Zählers, der an der Messstelle hängt — für die Meldung. */
  readonly meterLabel: string;
  /** Die Wohneinheit, der die Messstelle zugeordnet werden soll. */
  readonly to: string;
  /** Die Wohneinheit, der sie vorher zugeordnet war — `null`, wenn keiner. */
  readonly from: string | null;
}

export type UnitMovesOutcome =
  | { readonly status: "saved" }
  | {
      /** Eine Zuordnung oder die Fassung ist gescheitert; das Konzept ist unverändert. */
      readonly status: "failed";
      readonly error: unknown;
      /** Zuordnungen, die zurückgestellt wurden — sie stehen wieder auf `from`. */
      readonly restored: readonly UnitMove[];
      /** Zuordnungen, die sich nicht zurückstellen ließen — sie stehen jetzt auf `to`. */
      readonly stuck: readonly UnitMove[];
    };

/**
 * Ordnet nacheinander zu, speichert dann. Scheitert Zuordnung k, werden die
 * davor zurückgestellt; scheitert das Speichern, alle. `assign` wirft bei einem
 * Fehler, ebenso `save`.
 */
export async function saveWithUnitMoves(
  moves: readonly UnitMove[],
  assign: (meteringPointId: string, siteAreaId: string | null) => Promise<void>,
  save: () => Promise<void>,
): Promise<UnitMovesOutcome> {
  const done: UnitMove[] = [];
  const rollback = async (error: unknown): Promise<UnitMovesOutcome> => {
    const restored: UnitMove[] = [];
    const stuck: UnitMove[] = [];
    for (const move of [...done].reverse()) {
      try {
        await assign(move.meteringPointId, move.from);
        restored.unshift(move);
      } catch {
        stuck.unshift(move);
      }
    }
    return { status: "failed", error, restored, stuck };
  };

  for (const move of moves) {
    try {
      await assign(move.meteringPointId, move.to);
    } catch (error) {
      return rollback(error);
    }
    done.push(move);
  }
  try {
    await save();
  } catch (error) {
    return rollback(error);
  }
  return { status: "saved" };
}

/**
 * Der Satz zu Zuordnungen, die stehen geblieben sind — `null`, wenn keine.
 * Er nennt jeden Zähler und seine Wohneinheit, denn das ist, was jetzt gilt.
 */
export function stuckMovesMessage(
  stuck: readonly UnitMove[],
  areaName: (id: string) => string,
): string | null {
  if (stuck.length === 0) return null;
  const items = stuck.map(
    (move) =>
      `„${move.meterLabel}" ist jetzt der Wohneinheit „${areaName(move.to)}" zugeordnet` +
      (move.from === null ? " (vorher keiner)" : ` (vorher „${areaName(move.from)}")`),
  );
  return `Das Messkonzept wurde nicht gespeichert, und die Zuordnung ließ sich nicht zurücksetzen: ${items.join("; ")}. Du kannst sie am Zähler ändern.`;
}
